import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash, Download, User } from "@phosphor-icons/react";
import { toast } from "sonner";
import AttachmentsButton from "@/components/AttachmentsButton";
import { usePermissions } from "@/lib/permissions";
import CustomerProfilePanel from "@/components/orders/CustomerProfilePanel";
import QuickProductDialog from "@/components/orders/QuickProductDialog";
import ItemsTable from "@/components/orders/ItemsTable";

const STATUS_TONE = {
  draft: "bg-secondary text-muted-foreground",
  sent: "bg-primary/10 text-primary",
  paid: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  partial: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
  overdue: "bg-destructive/10 text-destructive",
  cancelled: "bg-muted text-muted-foreground line-through",
};

export default function Invoices() {
  const { can } = usePermissions();
  const { user } = useAuth();
  const canProducts = can("inventory.view");
  const canEditCustomer = can("customers.edit");
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [team, setTeam] = useState([]);
  const [units, setUnits] = useState([]);
  const [org, setOrg] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    customerId: "",
    orderId: "",
    items: [],
    discount: 0,
    assignedTo: "",
    invoice_date: new Date().toISOString().slice(0, 10),
    due_date: "",
    payment_terms: "Due on receipt",
    notes: "",
  });
  const [filterStatus, setFilterStatus] = useState("all");
  const [quickCreateFor, setQuickCreateFor] = useState(null);

  const load = async () => {
    const safe = (p) => p.catch(() => ({ data: [] }));
    const [inv, c, p, o, or, t, u] = await Promise.all([
      safe(api.get("/invoices")),
      safe(api.get("/customers", { params: { scope_mode: "tenant" } })),
      canProducts ? safe(api.get("/products")) : Promise.resolve({ data: [] }),
      safe(api.get("/organization")),
      safe(api.get("/orders")),
      safe(api.get("/users")),
      safe(api.get("/lookups/product_unit")),
    ]);
    setInvoices(inv.data); setCustomers(c.data); setProducts(p.data);
    setOrg(o.data || {}); setOrders(or.data); setTeam(t.data); setUnits(u.data);
  };
  useEffect(() => { load(); }, []);   // eslint-disable-line

  const patchForm = (patch) => setForm((f) => ({ ...f, ...patch }));
  const addItem = () => patchForm({ items: [...form.items, { product_id: "", name: "", qty: 1, price: 0, gst: 18, hsn: "", unit: "PCS" }] });
  const pickOrder = (oid) => {
    const o = orders.find((x) => x.id === oid);
    if (!o) return patchForm({ orderId: oid });
    patchForm({
      orderId: oid,
      customerId: o.customer_id,
      items: (o.items || []).map((it) => ({ product_id: it.product_id || "", name: it.name, qty: it.qty, price: it.price, gst: it.gst, hsn: it.hsn || "", unit: it.unit || "PCS" })),
    });
  };
  const resetForm = () => setForm({
    customerId: "", orderId: "", items: [], discount: 0, assignedTo: "",
    invoice_date: new Date().toISOString().slice(0, 10), due_date: "",
    payment_terms: "Due on receipt", notes: "",
  });

  const subtotal = form.items.reduce((s, it) => s + (it.qty || 0) * (it.price || 0), 0);
  const gstAmt = form.items.reduce((s, it) => s + (it.qty || 0) * (it.price || 0) * ((it.gst || 0) / 100), 0);
  const total = Math.max(0, subtotal + gstAmt - (+form.discount || 0));

  const create = async () => {
    const cust = customers.find((c) => c.id === form.customerId);
    if (!cust) return toast.error("Pick a customer");
    if (!form.items.length) return toast.error("Add at least one item");
    try {
      await api.post("/invoices", {
        customer_id: cust.id, customer_name: cust.customer_name,
        order_id: form.orderId || null,
        order_number: orders.find((o) => o.id === form.orderId)?.number || "",
        items: form.items, discount: +form.discount || 0,
        subtotal: +subtotal.toFixed(2), gst_amount: +gstAmt.toFixed(2),
        total: +total.toFixed(2), balance: +total.toFixed(2),
        invoice_date: form.invoice_date, due_date: form.due_date || null,
        payment_terms: form.payment_terms, notes: form.notes, status: "draft",
        assigned_to: form.assignedTo || null,
      });
      toast.success("Invoice created");
      setOpen(false); resetForm(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const setStatus = async (inv, status) => {
    try {
      const patch = { status };
      if (status === "paid") { patch.amount_paid = inv.total; patch.balance = 0; }
      if (status === "sent" && !inv.amount_paid) patch.balance = inv.total;
      await api.put(`/invoices/${inv.id}`, patch);
      toast.success(`Marked ${status}`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.number}?`)) return;
    try { await api.delete(`/invoices/${inv.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const printInvoice = async (inv) => {
    try {
      const r = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank");
      if (!w) {
        const a = document.createElement("a");
        a.href = url; a.download = `invoice_${inv.number}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        toast.info("Popup blocked — invoice downloaded instead");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate invoice PDF");
    }
  };

  const filtered = filterStatus === "all" ? invoices : invoices.filter((i) => i.status === filterStatus);
  const totals = {
    outstanding: invoices.filter((i) => ["sent", "partial", "overdue"].includes(i.status)).reduce((s, i) => s + (i.balance || 0), 0),
    paid: invoices.filter((i) => i.status === "paid").reduce((s, i) => s + (i.total || 0), 0),
    draft: invoices.filter((i) => i.status === "draft").length,
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex flex-col md:flex-row justify-between md:items-end gap-4">
        <div>
          <div className="overline text-muted-foreground mb-2">Billing</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight leading-none">Invoices</h1>
          <p className="text-sm text-muted-foreground mt-2">Bill customers, mark payments, and export branded PDFs.</p>
        </div>
        {can("invoices.create") && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="add-invoice-button">
                <Plus size={16} className="mr-2" />New Invoice
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
              <DialogHeader><DialogTitle>New Invoice</DialogTitle></DialogHeader>
              <div className="grid md:grid-cols-[1fr_320px] gap-4">
                <div className="space-y-4 py-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">CUSTOMER</Label>
                      <Select value={form.customerId} onValueChange={(v) => patchForm({ customerId: v })}>
                        <SelectTrigger data-testid="inv-customer"><SelectValue placeholder="Pick a customer" /></SelectTrigger>
                        <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.customer_name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">FROM ORDER (optional)</Label>
                      <Select value={form.orderId} onValueChange={pickOrder}>
                        <SelectTrigger data-testid="inv-order"><SelectValue placeholder="Copy items from…" /></SelectTrigger>
                        <SelectContent>
                          {orders.filter((o) => o.order_status !== "cancelled").map((o) => (
                            <SelectItem key={o.id} value={o.id}>{o.number} — {o.customer_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <Label className="text-xs">ITEMS</Label>
                      <Button size="sm" variant="outline" onClick={addItem} data-testid="inv-add-item"><Plus size={12} className="mr-1" />Item</Button>
                    </div>
                    <ItemsTable items={form.items} products={products} units={units}
                      onChange={(items) => patchForm({ items })} testidPrefix="inv-line"
                      onQuickCreate={(idx) => setQuickCreateFor(idx)} />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label className="text-xs">INVOICE DATE</Label><Input type="date" value={form.invoice_date} onChange={(e) => patchForm({ invoice_date: e.target.value })} /></div>
                    <div><Label className="text-xs">DUE DATE</Label><Input type="date" value={form.due_date} onChange={(e) => patchForm({ due_date: e.target.value })} /></div>
                    <div><Label className="text-xs">PAYMENT TERMS</Label><Input value={form.payment_terms} onChange={(e) => patchForm({ payment_terms: e.target.value })} placeholder="Net 15 days" /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">ASSIGN TO</Label>
                      <Select value={form.assignedTo || "__none__"} onValueChange={(v) => patchForm({ assignedTo: v === "__none__" ? "" : v })}>
                        <SelectTrigger data-testid="inv-assigned-to"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Unassigned</SelectItem>
                          {team.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs">DISCOUNT ₹</Label><Input type="number" value={form.discount} onChange={(e) => patchForm({ discount: e.target.value })} data-testid="inv-discount" /></div>
                    <div>
                      <Label className="text-xs">CREATED BY</Label>
                      <div className="h-10 px-3 flex items-center gap-2 rounded-md border border-border bg-secondary/40 text-sm text-muted-foreground" data-testid="inv-created-by">
                        <User size={12} weight="duotone" />
                        <span className="truncate">{user?.name || user?.email}</span>
                        <span className="ml-auto text-[10px] uppercase">auto</span>
                      </div>
                    </div>
                  </div>
                  <Input placeholder="Notes" value={form.notes} onChange={(e) => patchForm({ notes: e.target.value })} data-testid="inv-notes" />
                  <div className="text-right" data-testid="inv-total">
                    <div className="text-xs text-muted-foreground">Subtotal ₹{subtotal.toFixed(2)} + GST ₹{gstAmt.toFixed(2)}</div>
                    <div className="font-heading font-bold text-xl">Total ₹{total.toFixed(2)}</div>
                  </div>
                </div>
                <div className="md:sticky md:top-2 md:h-fit">
                  <CustomerProfilePanel customerId={form.customerId} canEditCustomer={canEditCustomer} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={create} data-testid="save-invoice-button">Create invoice</Button>
              </DialogFooter>
              <QuickProductDialog
                open={quickCreateFor !== null}
                onOpenChange={(v) => !v && setQuickCreateFor(null)}
                units={units}
                onCreated={(p) => {
                  setProducts(prev => [p, ...prev]);
                  patchForm({ items: form.items.map((it, idx) => idx === quickCreateFor
                    ? { ...it, product_id: p.id, name: p.name, price: p.price, gst: p.gst, unit: p.unit || "PCS" }
                    : it) });
                  setQuickCreateFor(null);
                }}
              />
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5" data-testid="stat-outstanding">
          <div className="overline text-muted-foreground">Outstanding</div>
          <div className="font-heading text-3xl font-bold mt-2">₹{totals.outstanding.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</div>
        </Card>
        <Card className="p-5" data-testid="stat-paid">
          <div className="overline text-muted-foreground">Paid this year</div>
          <div className="font-heading text-3xl font-bold mt-2">₹{totals.paid.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</div>
        </Card>
        <Card className="p-5" data-testid="stat-drafts">
          <div className="overline text-muted-foreground">Drafts</div>
          <div className="font-heading text-3xl font-bold mt-2">{totals.draft}</div>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <div className="p-4 border-b border-border/40 flex items-center justify-between gap-3 flex-wrap">
          <div className="font-heading font-bold text-sm">All invoices · {invoices.length}</div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40" data-testid="inv-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground tracking-wider">
              <tr>
                <th className="text-left p-4">Invoice #</th>
                <th className="text-left p-4">Customer</th>
                <th className="text-left p-4">Date</th>
                <th className="text-left p-4">Due</th>
                <th className="text-right p-4">Total</th>
                <th className="text-left p-4">Status</th>
                <th className="text-left p-4">Assigned</th>
                <th className="text-left p-4">Created By</th>
                <th className="text-right p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inv) => (
                <tr key={inv.id} className="border-t border-border/40" data-testid={`invoice-${inv.id}`}>
                  <td className="p-4 font-mono text-xs">
                    {inv.number}
                    {inv.order_number && <div className="text-[10px] text-muted-foreground mt-0.5">from {inv.order_number}</div>}
                  </td>
                  <td className="p-4 font-medium">{inv.customer_name}</td>
                  <td className="p-4 text-xs">{inv.invoice_date || new Date(inv.created_at).toLocaleDateString("en-IN")}</td>
                  <td className="p-4 text-xs">{inv.due_date || "—"}</td>
                  <td className="p-4 text-right font-heading font-bold">₹{(inv.total || 0).toLocaleString("en-IN")}</td>
                  <td className="p-4">
                    {can("invoices.edit") ? (
                      <Select value={inv.status} onValueChange={(v) => setStatus(inv, v)}>
                        <SelectTrigger className={`h-8 text-xs w-28 ${STATUS_TONE[inv.status] || ""}`} data-testid={`inv-status-${inv.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">draft</SelectItem>
                          <SelectItem value="sent">sent</SelectItem>
                          <SelectItem value="partial">partial</SelectItem>
                          <SelectItem value="paid">paid</SelectItem>
                          <SelectItem value="overdue">overdue</SelectItem>
                          <SelectItem value="cancelled">cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className={`text-xs px-2 py-1 rounded-full ${STATUS_TONE[inv.status] || "bg-secondary"}`}>{inv.status}</span>
                    )}
                  </td>
                  <td className="p-4 text-xs" data-testid={`inv-assigned-${inv.id}`}>{inv.assigned_to_name || <span className="text-muted-foreground">—</span>}</td>
                  <td className="p-4 text-xs text-muted-foreground" data-testid={`inv-created-by-${inv.id}`}>{inv.created_by_name || "—"}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-1 justify-end">
                      <Button size="sm" variant="outline" onClick={() => printInvoice(inv)} data-testid={`inv-pdf-${inv.id}`}>
                        <Download size={12} className="mr-1" />PDF
                      </Button>
                      <AttachmentsButton entityType="invoice" entityId={inv.id} label={`Files — ${inv.number}`} testId={`attach-invoice-${inv.id}`} />
                      {can("invoices.delete") && (
                        <button onClick={() => remove(inv)} className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive" data-testid={`delete-invoice-${inv.id}`} title="Delete"><Trash size={14} weight="duotone" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!filtered.length && (
                <tr><td colSpan={9} className="p-10 text-center text-muted-foreground">
                  {invoices.length ? "No invoices match this filter." : "No invoices yet — create your first."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
